import { RootState } from "store";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { addCommaToNumber, getQuarterDates, getDateObject } from "utils";
import AllTypeIcon from "../../../assets/img/icons/Campaign/AllType.svg";
import {
  reorderCampaignsAndEmails,
  resetEmailSearch,
  resetSelectedEmails,
  searchAnalyticsDropdownAssets,
  selectAsset,
  setAssetsFilter,
  selectAllEmails,
  unselectAllEmails,
  analyticsDatesFilterChange,
  updateFirstTimeSearch,
  selectWorkflowAsset,
  selectAllWorkflowEmails,
  unselectAllWorkflowEmails,
  setWorkflowAssetsFilter,
} from "../Dashboard/actions";
import { IAssetDropdownItem } from "../interfaces";
import SelectFilterWithIcon from "./SelectFilterWithIcon";

interface IAssetsFiltersDropdown {
  closeDropDownHandler: () => void;
}

const AssetsFiltersDropdown: React.FC<IAssetsFiltersDropdown> = (props) => {
  const assetFilters = useSelector((state: RootState) => state.analyticsDashboard.assetFilters);
  const isAssetSearch = useSelector((state: RootState) => state.analyticsDashboard.isAssetSearch);
  const filteredAssets = useSelector((state: RootState) => state.analyticsDashboard.filteredAssets);
  const searchedAssets = useSelector((state: RootState) => state.analyticsDashboard.searchedAssets);
  const assetSearchTerm = useSelector((state: RootState) => state.analyticsDashboard.assetSearchTerm);
  const firstTimeSearch = useSelector((state: RootState) => state.analyticsDashboard.firstTimeSearch);
  const selectedAssetsIds = useSelector((state: RootState) => state.analyticsDashboard.selectedAssetsIds);
  const isTestEmailFilter = useSelector((state: RootState) => state.analyticsDashboard.isTestEmailFilter);
  const firstAllDataShown = useSelector((state: RootState) => state.analyticsDashboard.firstAllDataShown);
  const selectedDatesObject = useSelector((state: RootState) => state.analyticsGraph.selectedDatesObject);
  const assetsLoading = useSelector((state: RootState) => state.analyticsDashboard.campaignsAssetsLoading);
  const selectedCampaignsIds = useSelector((state: RootState) => state.analyticsDashboard.selectedCampaignsIds);
  const campaignsLimitReached = useSelector((state: RootState) => state.analyticsDashboard.campaignsLimitReached);
  const { isShareProfile, isDailyIQ, isWeeklyAccountInsights, isWorkflow } = useSelector(
    (state: RootState) => state.analyticsDashboard
  );
  const AllCampaignsFilterCheck = useSelector(
    (state: RootState) => state.analyticsDashboard.campaignsFilters.isAllCampaigns
  );

  const handleSelectAllEmails = (flag: boolean) => {
    if (flag) {
      selectAllEmails();
    } else {
      unselectAllEmails();
    }
  };

  const handleSelectAllWorkflowEmails = (flag: boolean) => {
    if (flag) {
      selectAllWorkflowEmails();
    } else {
      unselectAllWorkflowEmails();
    }
  };

  useEffect(() => {
    return () => {
      resetSelectedEmails();
      searchAnalyticsDropdownAssets("");
    };
  }, []);

  useEffect(() => {
    // added this useEffect to handle the case when user makes search first time after landing on the Analytics in the
    // date filter at top, we will show “All time”, unless user applies a different date filter
    if (!firstTimeSearch && assetSearchTerm) {
      analyticsDatesFilterChange({ label: "All time", value: "all" });
      updateFirstTimeSearch();
    }
  }, [assetSearchTerm]);

  useEffect(() => {
    // added this useEffect to handle the case If the searched email is out of the default date
    // filter, i.e. 1 Qtr, then in the date filter at top, we will show “All time”
    if (assetSearchTerm && searchedAssets.length > 0) {
      const datesObject = getQuarterDates();
      for (let asset of searchedAssets) {
        let date = new Date(asset.dateTime);
        if (date < new Date(datesObject.start_date) || date > new Date(datesObject.end_date)) {
          analyticsDatesFilterChange({ label: "All time", value: "all" });
          break;
        }
      }
    }
  }, [assetSearchTerm]);

  function handleSelectAsset(asset: IAssetDropdownItem) {
    selectAsset(asset.id, asset.campaignId);
    // isPresent uses to check if the asset is selected or not if not selected that means user want to add it then we check the dates
    // selectedCampaignsIds uses to see the limit of the selected campaigns should not be reached.
    // not using campaignsLimitReached becuase by default its false even the inital data is being set.
    let isPresent = selectedAssetsIds.includes(asset.id);
    if (!isPresent && selectedCampaignsIds.length !== 4) {
      let datesObject: any = getDateObject(selectedDatesObject.value);
      let date = new Date(asset.dateTime);
      if (date < new Date(datesObject.start_date)) {
        //  an email that dates earlier than the applied date filter
        analyticsDatesFilterChange({ label: "All time", value: "all" }); // calling the action to change the filter
      }
    }
  }

  function handleSelectWorkflowAsset(asset: IAssetDropdownItem) {
    selectWorkflowAsset(asset.id, asset.campaignId);

    let isPresent = selectedAssetsIds.includes(asset.id);
    if (!isPresent) {
      let datesObject: any = getDateObject(selectedDatesObject.value);
      let date = new Date(asset.dateTime);
      if (date < new Date(datesObject.start_date)) {
        //  an email that dates earlier than the applied date filter
        analyticsDatesFilterChange({ label: "All time", value: "all" }); // calling the action to change the filter
      }
    }
  }

  return (
    <SelectFilterWithIcon
      className="analytics-filter-dropdown"
      label={
        selectedAssetsIds.length > 0
          ? `${addCommaToNumber((selectedAssetsIds?.length ?? 0).toString() ?? "")} Email(s) selected`
          : "Emails"
      }
      icon={AllTypeIcon}
      searchTitle="Search emails..."
      filterTitle="Filter Emails"
      filters={assetFilters}
      idPrefix="email-01"
      items={
        isWorkflow ? filteredAssets : isAssetSearch ? searchedAssets : filteredAssets
      }
      onSearchInput={(value: any) => {
        searchAnalyticsDropdownAssets(value);
      }}
      setCheckBox={(value: boolean) => {
        isWorkflow ? setWorkflowAssetsFilter(value) : setAssetsFilter(value);
      }}
      setSelected={isWorkflow ? handleSelectWorkflowAsset : handleSelectAsset}
      selectedItems={selectedAssetsIds}
      onClearSearch={resetEmailSearch}
      dropdownClass={`dropdown-menu-wrapper`}
      dropdownType="Emails"
      assetSelectedMessage={campaignsLimitReached}
      onClose={() => {
        reorderCampaignsAndEmails();
        props.closeDropDownHandler();
      }}
      showSentBy
      // it will disabled all the emails in Email dropdown in case of share profile, weekly accounts insights and daily iq
      isShareProfile={isShareProfile || isWeeklyAccountInsights || isDailyIQ}
      hideShareProfile
      hideDailyIQ
      hideSearch={isWorkflow}
      isWorkflow={isWorkflow}
      showSelectAll={true}
      hideWeeklyAccountInsights
      loading={assetsLoading}
      isTestEmailFilter={isTestEmailFilter}
      disableDropdown={!firstAllDataShown || isShareProfile || isWeeklyAccountInsights || isDailyIQ || AllCampaignsFilterCheck}
      handleSelectAll={isWorkflow ? handleSelectAllWorkflowEmails : handleSelectAllEmails}
    />
  );
};

export default AssetsFiltersDropdown;
